import { SpreadGroundImage } from 'editor/src/store/design/types';

import { captureMessage } from 'editor/src/logger';

// Ground assets carry the print area's rect (left/top/width/height) inside their
// own image. A mask can only say which parts of the print area are printable
// when its image actually covers that print area — a panel template does, since
// the two coincide. A wrap-around product instead maps its print area onto a
// photo of one face of the product, so the print area runs off the sides of the
// image; there the mask marks what the camera can see, while the artwork beyond
// it still prints, just out of shot. Clipping to that would hide most of the
// design. Anything we cannot measure keeps the mask, so only a print area proven
// to escape its image opts out.
//
// The tolerance is proportional rather than a pixel or two: the two cases sit far
// apart (a wrap overhangs by ~40% of its image, a panel by nothing), so a slack
// this wide still separates them while absorbing whatever rounding the asset
// pipeline applies when it generates a template.
const EDGE_TOLERANCE_RATIO = 0.02;

// Dropping a mask changes what the customer sees, and the geometry that decides it
// comes from the product templates rather than this repo — so each distinct mask
// that opts out is reported once, and only once, per session.
const reportedMasks = new Set<string>();

function reportOnce(ground: SpreadGroundImage): void {
  const id = `${ground.name}|${ground.url}`;
  if (reportedMasks.has(id)) {
    return;
  }
  reportedMasks.add(id);
  captureMessage('Spread mask ignored: print area escapes the mask image', {
    extra: {
      name: ground.name,
      url: ground.url,
      left: ground.left,
      top: ground.top,
      width: ground.width,
      height: ground.height,
      imgWidth: ground.imgWidth,
      imgHeight: ground.imgHeight,
    },
  });
}

function coversPrintArea(ground: SpreadGroundImage): boolean {
  const { left, top, width, height, imgWidth, imgHeight } = ground;

  if (!imgWidth || !imgHeight || !width || !height) {
    return true;
  }

  const toleranceX = imgWidth * EDGE_TOLERANCE_RATIO;
  const toleranceY = imgHeight * EDGE_TOLERANCE_RATIO;

  const covers =
    left >= -toleranceX &&
    top >= -toleranceY &&
    left + width <= imgWidth + toleranceX &&
    top + height <= imgHeight + toleranceY;

  if (!covers) {
    reportOnce(ground);
  }

  return covers;
}

export default coversPrintArea;
